<template>
    <div class="quickpay">
        <QuickpayPaymentOverviewSuccess
            v-if="currentView === 'success'"
            :data="data"
            :hash="hash"
            @back="handleBack('overview')"
        />
        <QuickpayPaymentOverviewCard
            v-else-if="currentView === 'stripe'"
            :hash="hash"
            :deadlineId="selectedDeadlineId"
            @payment-success="setView('success')"
            @back="handleBack('overview')"
        />
        <template v-else>
            <div class="quickpay__container">
                <QuickpayPDFViewer
                    v-if="isLargeScreen"
                    :src="data.document.pdf_link"
                    :document="data.document"
                    :hash="hash"
                />
                <div class="quickpay__container-content">
                    <QuickpayEsignOverview
                        v-if="checkStep('esign_link')"
                        :data="data"
                        :hash="hash"
                        :hasFooter="hasQuickpayV2PoweredSellsyFeature"
                    />
                    <QuickpayDeadlineSelection
                        v-else-if="shouldShowDeadlineSelection"
                        :data="data"
                        :hasFooter="hasQuickpayV2PoweredSellsyFeature"
                        :preselectedDeadlineId="selectedDeadlineId"
                        :autoExpand="showDeadlineSelection"
                        @select-deadline="handleDeadlineSelection"
                        @pay-whole-document="handlePayWholeDocument"
                    />
                    <QuickpayPaymentOverview
                        v-else-if="checkStep('payments')"
                        :data="data"
                        :hash="hash"
                        :deadlineId="selectedDeadlineId"
                        :selectedDeadline="selectedDeadline"
                        @show-stripe-payment="setView('stripe')"
                        @change-deadline="handleChangeDeadline"
                    />
                    <QuickpayOverview
                        v-else
                        :data="data"
                        :hasFooter="hasQuickpayV2PoweredSellsyFeature"
                    />
                </div>
            </div>
        </template>
        <FooterPoweredBySellsy v-if="hasQuickpayV2PoweredSellsyFeature" :fixedOnMobile="false" />
    </div>
</template>

<script>
import QuickpayPDFViewer from 'vcomponents/quickpay/QuickpayPDFViewer.vue';
import FooterPoweredBySellsy from 'vcomponents/common/FooterPoweredBySellsy.vue';
import QuickpayOverview from './QuickpayOverview.vue';
import QuickpayDeadlineSelection from './QuickpayDeadlineSelection.vue';
import QuickpayEsignOverview from 'vcomponents/quickpay/esign/QuickpayEsignOverview.vue';
import QuickpayPaymentOverview from 'vcomponents/quickpay/payment/QuickpayPaymentOverview.vue';
import QuickpayPaymentOverviewSuccess from 'vcomponents/quickpay/payment/QuickpayPaymentOverviewSuccess.vue';
import QuickpayPaymentOverviewCard from 'vcomponents/quickpay/payment/QuickpayPaymentOverviewCard.vue';
import Feature from 'Services/Feature';
import { initVisitTracking } from 'api/quickpay/quickpayTrackingService.js';

export default {
    components: {
        QuickpayPDFViewer,
        FooterPoweredBySellsy,
        QuickpayOverview,
        QuickpayDeadlineSelection,
        QuickpayEsignOverview,
        QuickpayPaymentOverview,
        QuickpayPaymentOverviewSuccess,
        QuickpayPaymentOverviewCard,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        hash: {
            type: String,
            required: true,
        },
        deadlineId: {
            type: Number,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            currentView: 'overview',
            isLargeScreen: window.innerWidth > 1023,
            selectedDeadlineId: this.deadlineId,
            selectedDeadline: null,
            showDeadlineSelection: false,
            cleanupVisitTracking: null,
        };
    },
    computed: {
        hasQuickpayV2PoweredSellsyFeature() {
            return Feature.isEnabled('quickpay-v2-powered-by-sellsy');
        },
        hasDeadlines() {
            return this.data.deadlines && this.data.deadlines.length > 0;
        },
        shouldShowDeadlineSelection() {
            return (
                this.checkStep('payments') &&
                this.hasDeadlines &&
                (!this.selectedDeadlineId || this.showDeadlineSelection)
            );
        },
    },
    watch: {
        'data.step'() {
            this.updateViewFromStep();
        },
    },
    mounted() {
        this.handleResize();
        window.addEventListener('resize', this.handleResize);
        this.updateViewFromStep();
        this.cleanupVisitTracking = initVisitTracking(
            this.data.document.doctype,
            this.data.document.id,
            this.data.document.corpid,
        );
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
        if (this.cleanupVisitTracking) {
            this.cleanupVisitTracking();
        }
    },
    methods: {
        checkStep(step) {
            return this.data && this.data.step === step;
        },
        updateViewFromStep() {
            if (this.checkStep('payment_success')) {
                this.setView('success');
            }
        },
        setView(view) {
            this.currentView = view;
        },
        handleResize() {
            this.isLargeScreen = window.innerWidth > 1023;
        },
        handleBack(view) {
            if (window.location.pathname.includes('/success')) {
                const newPath = window.location.pathname.replace('/success', '');
                window.history.pushState(
                    {},
                    '',
                    newPath + window.location.search + window.location.hash,
                );
            }
            this.setView(view);
        },
        handleDeadlineSelection(deadline) {
            this.selectedDeadlineId = deadline.id;
            this.selectedDeadline = deadline;
            this.showDeadlineSelection = false;
        },
        handlePayWholeDocument() {
            // set a sentinel id to hide the deadline selection
            // and indicate "pay whole document" flow. We'll use -1 and
            // let the payment overview omit deadlineId when <= 0.
            this.selectedDeadlineId = -1;
            this.selectedDeadline = null;
            this.showDeadlineSelection = false;
        },
        handleChangeDeadline() {
            this.showDeadlineSelection = true;
        },
    },
};
</script>

<style lang="scss">
.global:has(.quickpay) {
    min-width: 0 !important;
}
#contentDiv.has-sidebar-closed:has(.quickpay) {
    padding: 0 !important;
}
#contentDiv:has(.quickpay) .contentMargin {
    height: auto;
}
</style>

<style lang="scss" scoped>
.quickpay {
    @media (max-width: 1023px) {
        min-height: 100dvh;
        display: flex;
        flex-direction: column;
    }

    &__container {
        height: 100%;
        display: flex;
        justify-content: space-evenly;
        padding: var(--spacing-3);

        &-content {
            width: 100%;
            max-width: 400px;

            @media (min-width: 1024px) {
                margin-top: 130px;
            }
        }

        @media (min-width: 1441px) {
            max-width: 1440px;
            margin: 0 auto;
        }

        @media (max-width: 1280px) {
            gap: var(--spacing-5);
        }

        @media (max-width: 1023px) {
            height: auto;
            align-items: normal;
            flex: 1;
        }
    }
}
</style>
